import React, { Component } from 'react';
import { flatMap, compact } from 'lodash';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import classNames from 'classnames';

import { DealerInfoList } from 'site-modules/dealerships/components/dealer-info-list/dealer-info-list';
import { DealerSocialLinks } from 'site-modules/dealerships/components/dealer-social-links/dealer-social-links';
import { GoogleStaticMap } from 'site-modules/shared/components/google-map-static/google-map-static';
import { getMergedAddress, getGoogleMapsUrl } from 'site-modules/shared/utils/dealer-details-utils';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { SecretsManagerStorage } from 'client/secrets-manager-storage';

import './dealer-about-info.scss';

const CARMAX_DEALERSHIP_NAME = 'carmax';
const CARMAX_URL =
  'https://www.carmax.com/articles/shopping-for-a-used-car-get-the-most-bang-for-your-buck?utm_campaign=LG_OC&utm_source=LG_OC_EDM&utm_medium=OC&utm_content=content';
const MAP_HEIGHT = 200;
const GOOGLE_MAPS_DEALER_SHIP_API_KEY_SECRET = { name: 'googleMapsDealershipApiKey', key: 'ApiKey' };

export function DealerDistinguishingFeature(props) {
  const { description } = props;
  return (
    <div className="dealer-distinguishing-feature my-1">
      <div className="feature-icon-wrap text-blue-50">
        <span className="icon-star-full" aria-hidden />
      </div>
      <div className="feature-description-wrap">{description}</div>
    </div>
  );
}
DealerDistinguishingFeature.propTypes = {
  description: PropTypes.string,
};

DealerDistinguishingFeature.defaultProps = {
  description: '',
};

export class DealerAboutInfoUI extends Component {
  getSplittedAboutTextByParagraphs = () => {
    const { about } = this.props.dealerInfo;
    if (!about) return '';
    return about.split('\n');
  };
  /**
   * Check if parent dealership name contains a name for checking
   * @param checkName
   * @returns {boolean}
   */
  parentDealershipCheck = checkName => {
    const { parentDealershipName } = this.props.dealerInfo;
    return parentDealershipName && parentDealershipName.toLowerCase().indexOf(checkName) > -1;
  };

  buildGoogleMapLink = (address, location, googleApiKey) =>
    location.lat && location.lon ? (
      <RenderWhenViewable placeholder={<div className="map-placeholder" style={{ height: MAP_HEIGHT }} />}>
        <div className="d-flex justify-content-center">
          <a
            target="_blank"
            href={getGoogleMapsUrl(address)}
            rel="noopener noreferrer"
            id="about-us-map"
            data-tracking-id="ddp_view_dealer_hours_and_directions"
          >
            <GoogleStaticMap
              className="dealer-map"
              latitude={location.lat}
              longitude={location.lon}
              address={getMergedAddress(address)}
              size={{ width: 350, height: MAP_HEIGHT }}
              zoom={15}
              apiKey={googleApiKey}
            />
          </a>
        </div>
      </RenderWhenViewable>
    ) : null;

  buildAboutText = (paragraph, index) => <p key={`dealer_about_text_paragraph#${index + 1}`}>{paragraph}</p>;

  buildDealerDistinguishingFeature = (description, index) => (
    <DealerDistinguishingFeature key={`${description.slice(5) + index}`} description={description} />
  );

  render() {
    const {
      about,
      amenities,
      distinguishingFeatures = [],
      languages,
      socialLinks = [],
      location = {},
      address,
    } = this.props.dealerInfo;

    // TODO: update parent dealership logic when dealer info API will be updated with following fields
    const isCarmax = this.parentDealershipCheck(CARMAX_DEALERSHIP_NAME);
    const hasAboutText = isCarmax || about;
    const hasAmenities = !!(amenities && amenities.length);
    const hasLanguages = !!(languages && languages.length);
    const featuresDescriptions = compact(flatMap(distinguishingFeatures, 'description'));
    const hasDistinguishingFeatures = !!featuresDescriptions.length;
    const twoColumnsMode = hasAboutText || hasDistinguishingFeatures || hasAmenities || hasLanguages;
    const googleApiKeyObject = SecretsManagerStorage.get(GOOGLE_MAPS_DEALER_SHIP_API_KEY_SECRET.name, '');
    const googleApiKey = googleApiKeyObject ? googleApiKeyObject[GOOGLE_MAPS_DEALER_SHIP_API_KEY_SECRET.key] : '';

    // render only dealer social links if other information is empty
    if (!twoColumnsMode) {
      return (
        <Container className="dealer-about-info text-center mb-3" id="edm-entry-dealer-about">
          <div className="titles text-info text-center mb-2">
            <div className="size-30 fw-bold">About our dealership</div>
          </div>
          {this.buildGoogleMapLink(address, location, googleApiKey)}
          <DealerSocialLinks socialLinks={socialLinks} />
        </Container>
      );
    }

    const aboutText = compact(this.getSplittedAboutTextByParagraphs());
    return (
      <Container className="dealer-about-info mb-3" id="edm-entry-dealer-about">
        <div className="titles text-info text-center mb-2">
          <div className="size-30 fw-bold">About our dealership</div>
        </div>
        <Row className="justify-content-center">
          <Col xs={12} md={{ size: 7, offset: 1 }} className="main-info">
            {isCarmax && (
              <p className="parent-dealership-text">
                When you&apos;re shopping used cars, you want the most car for the best price. Let CarMax show you five
                ways to get a great used car without paying a premium:
                <a className="carmax-linkout" href={CARMAX_URL}>
                  {' '}
                  CarMax Used Car Buying Tips
                </a>
              </p>
            )}
            {!!aboutText.length && <div className="about-text mb-2">{aboutText.map(this.buildAboutText)}</div>}
            {hasDistinguishingFeatures && (
              <div className="distinguishing-features">
                <div className="display-3 fw-bold">What sets us apart</div>
                <div>{featuresDescriptions.map(this.buildDealerDistinguishingFeature)}</div>
              </div>
            )}
            {!(hasAboutText || hasDistinguishingFeatures) && this.buildGoogleMapLink(address, location, googleApiKey)}
            <DealerSocialLinks socialLinks={socialLinks} />
          </Col>
          <Col xs={12} md={4}>
            {(hasAboutText || hasDistinguishingFeatures) && this.buildGoogleMapLink(address, location, googleApiKey)}
            <DealerInfoList
              list={amenities}
              heading="Amenities"
              className={classNames('amenities-list', {
                'mt-md-1_5': (hasAboutText || hasDistinguishingFeatures) && location.lat && location.lon,
              })}
              trackingValue="amenities"
            />
            <DealerInfoList
              list={languages}
              heading={`Languages Spoken (${languages && languages.length})`}
              columns={2}
              previewSize={4}
              className="languages-list"
              trackingValue="languages"
            />
          </Col>
        </Row>
      </Container>
    );
  }
}

DealerAboutInfoUI.propTypes = {
  dealerInfo: PropTypes.shape({
    parentDealershipName: PropTypes.string,
    about: PropTypes.string,
    amenities: PropTypes.arrayOf(PropTypes.string),
    distinguishingFeatures: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        description: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    location: PropTypes.shape({
      lat: PropTypes.number,
      lon: PropTypes.number,
    }),
    address: PropTypes.shape({
      street: PropTypes.string,
      city: PropTypes.string,
      stateCode: PropTypes.string,
      zip: PropTypes.string,
    }),
    languages: PropTypes.arrayOf(PropTypes.string),
    socialLinks: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      })
    ),
  }),
};

DealerAboutInfoUI.defaultProps = {
  dealerInfo: {},
};
